import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";

export const NEWSLETTER_CONSENT_TEXT =
  "Aceito receber ofertas e novidades por e-mail do Shopping do Bem.";

const schema = z.object({
  name: z.string().trim().min(2).max(80),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .max(255)
    .refine(
      (e) => !/@([^@]+\.)?example\.(com|org|net)$|\.(test|invalid|localhost|example)$/.test(e),
      "E-mail inválido",
    ),
  consent: z.literal(true),
  website: z.string().max(0).optional(), // honeypot
  sourceType: z.enum(["brand", "footer", "coupons", "category", "modal"]),
  sourceSlug: z.string().trim().max(120).optional(),
});

async function sha256(v: string) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => schema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const ip =
      getRequestHeader("cf-connecting-ip") ||
      getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim() ||
      "unknown";
    const ipHash = await sha256(`nl:${ip}`);

    const since = new Date(Date.now() - 3600_000).toISOString();
    const { count } = await (supabaseAdmin as any)
      .from("newsletter_subscribers")
      .select("id", { count: "exact", head: true })
      .eq("ip_hash", ipHash)
      .gte("updated_at", since);
    if ((count ?? 0) >= 5) return { ok: false as const, error: "rate_limited" };

    const { error } = await (supabaseAdmin as any).from("newsletter_subscribers").upsert(
      {
        name: data.name,
        email: data.email,
        source_type: data.sourceType,
        source_slug: data.sourceSlug ?? null,
        consent_text: NEWSLETTER_CONSENT_TEXT,
        consent_at: new Date().toISOString(),
        ip_hash: ipHash,
        unsubscribed_at: null,
      },
      { onConflict: "email" },
    );
    if (error) {
      console.error("newsletter upsert", error);
      return { ok: false as const, error: "server" };
    }
    return { ok: true as const };
  });
